export type ItemRow = {
  entryKey?: string;
  productId: string;
  productQuery: string;
  quantity: string;
  unitPrice: string;
  lineTotal: string;
  weightType: string;
  packageCount: string;
  packageWeight: string;
  packagePrice: string;
  lotNumber: string;
  manufactureDate: string;
  expirationDate: string;
};

export type RowUpdateSource =
  | 'quantity'
  | 'unitPrice'
  | 'lineTotal'
  | 'packageCount'
  | 'packageWeight'
  | 'packagePrice'
  | 'other';

const normalizeCalcInput = (value: string) => (value || '').replace(/\s+/g, '').replace(/,/g, '.');

export const evaluateCalcInput = (value: string): number | null => {
  const normalized = normalizeCalcInput(value);
  if (!normalized || !/^[0-9+\-*/().]+$/.test(normalized)) return null;

  const tokens = normalized.match(/\d*\.?\d+|[()+\-*/]/g);
  if (!tokens || tokens.join('') !== normalized) return null;

  let index = 0;

  const parseExpression = (): number | null => {
    let left = parseTerm();
    if (left === null) return null;
    while (index < tokens.length && (tokens[index] === '+' || tokens[index] === '-')) {
      const op = tokens[index++];
      const right = parseTerm();
      if (right === null) return null;
      left = op === '+' ? left + right : left - right;
    }
    return left;
  };

  const parseTerm = (): number | null => {
    let left = parseFactor();
    if (left === null) return null;
    while (index < tokens.length && (tokens[index] === '*' || tokens[index] === '/')) {
      const op = tokens[index++];
      const right = parseFactor();
      if (right === null) return null;
      if (op === '/' && right === 0) return null;
      left = op === '*' ? left * right : left / right;
    }
    return left;
  };

  const parseFactor = (): number | null => {
    if (index >= tokens.length) return null;
    const token = tokens[index++];
    if (token === '+') return parseFactor();
    if (token === '-') {
      const value = parseFactor();
      return value === null ? null : -value;
    }
    if (token === '(') {
      const value = parseExpression();
      if (value === null || tokens[index] !== ')') return null;
      index += 1;
      return value;
    }
    const parsed = Number(token);
    return Number.isFinite(parsed) ? parsed : null;
  };

  const result = parseExpression();
  if (result === null || index !== tokens.length || !Number.isFinite(result)) return null;
  return result;
};

export const formatCalcValue = (value: number, fractionDigits: number) => {
  if (!Number.isFinite(value)) return '';
  return value.toFixed(fractionDigits).replace('.', ',');
};

export const hasPackageMode = (row: ItemRow) => {
  const packageCount = evaluateCalcInput(row.packageCount);
  const packageWeight = evaluateCalcInput(row.packageWeight);
  return packageCount !== null && packageCount > 0 && packageWeight !== null && packageWeight > 0;
};

export const getResolvedQuantity = (row: ItemRow) => {
  if (hasPackageMode(row)) {
    return (evaluateCalcInput(row.packageCount) || 0) * (evaluateCalcInput(row.packageWeight) || 0);
  }
  return evaluateCalcInput(row.quantity) || 0;
};

export const getResolvedUnitPrice = (row: ItemRow) => {
  if (hasPackageMode(row)) {
    const packagePrice = evaluateCalcInput(row.packagePrice);
    const packageWeight = evaluateCalcInput(row.packageWeight) || 0;
    if (packagePrice !== null && packageWeight > 0) return packagePrice / packageWeight;
  }
  const unitPrice = evaluateCalcInput(row.unitPrice);
  if (unitPrice !== null) return unitPrice;

  const quantity = getResolvedQuantity(row);
  const lineTotal = evaluateCalcInput(row.lineTotal);
  if (lineTotal !== null && quantity > 0) return lineTotal / quantity;
  return 0;
};

export const getRowComputedTotal = (row: ItemRow) =>
  getResolvedQuantity(row) * getResolvedUnitPrice(row);

export const syncItemRow = (
  row: ItemRow,
  patch: Partial<ItemRow>,
  source: RowUpdateSource = 'other'
): ItemRow => {
  const next = { ...row, ...patch };
  const quantity = getResolvedQuantity(next);
  const lineTotal = evaluateCalcInput(next.lineTotal);
  const packageCount = evaluateCalcInput(next.packageCount);

  if (source === 'lineTotal') {
    if (quantity > 0 && lineTotal !== null) {
      next.unitPrice = formatCalcValue(lineTotal / quantity, 6);
      if (hasPackageMode(next) && packageCount !== null && packageCount > 0) {
        next.packagePrice = formatCalcValue(lineTotal / packageCount, 6);
      }
    }
    return next;
  }

  if (source === 'unitPrice') {
    const unitPrice = evaluateCalcInput(next.unitPrice);
    next.lineTotal = quantity > 0 && unitPrice !== null
      ? formatCalcValue(quantity * unitPrice, 2)
      : '';
    return next;
  }

  const unitPrice = getResolvedUnitPrice(next);
  if (hasPackageMode(next)) {
    next.quantity = formatCalcValue(quantity, 4);
    next.unitPrice = formatCalcValue(unitPrice, 6);
    next.lineTotal = formatCalcValue(quantity * unitPrice, 2);
    return next;
  }

  if (source === 'quantity' && quantity > 0 && lineTotal !== null && evaluateCalcInput(next.unitPrice) === null) {
    next.unitPrice = formatCalcValue(lineTotal / quantity, 6);
    return next;
  }

  if (source === 'quantity' || source === 'packageCount' || source === 'packageWeight' || source === 'packagePrice') {
    next.lineTotal = quantity > 0 && Number.isFinite(unitPrice)
      ? formatCalcValue(quantity * unitPrice, 2)
      : '';
  }

  return next;
};
